<template>
    <div class="row">
        <div class="col-12">
            <div class="card mb-1 p-1">
                <DentitionPlan :dentition="dentition" :dentitionType="dentitionType" :billedDentition="billedDentition">
                    <template #dentition-type>
                        <div v-for="item in dentitionTypes" :key="item.value" class="form-check form-check-inline mt-0" :class="generalId ? 'disabled-element' : ''">
                            <input 
                                v-model="dentitionType" 
                                class="form-check-input" type="radio" 
                                name="inlineRadioOptions"
                                :id="`inlineRadio${item.value}`"
                                :value="item.value"
                                @change="onChangeDentitionType(item.value)"
                            />
                            <label class="form-check-label" :for="`inlineRadio${item.value}`">{{ item.name }}</label>
                        </div>
                    </template>
                </DentitionPlan>
            </div>
        </div>
        <div class="col-12">
            <div class="card mb-1 p-1">
                <TreatmentPlanCard 
                    :note="treatmentPlan"
                    :salesOrderId="salesOrderId"
                    :generalId="generalId"
                    :dentition="dentition"
                    :dentitionType="dentitionType"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import TreatmentPlanCard from '@/components/molecule/company/dental-os/service/middle-header/TreatmentPlan.vue';
import DentitionPlan from '@/components/molecule/company/dental-os/service/middle-header/DentitionPlan.vue';
import handleInventory from "@/services/modules/inventory";
import { computed } from "vue";
import { useRoute } from "vue-router";
import handlePurchase from "@/services/modules/purchase";
import { cloneDeep } from 'lodash';
import { pluck } from '@/services/utils/global';

const { getProduct } = handleInventory();
const showError = inject('showError');
const route = useRoute();
const { fetchSalesOrderById } = handlePurchase();
const props = defineProps({
    resourceDescriptions: {
        type: Array,
        default: []
    },
    treatmentPlan: {
        type: Object,
        default: () => {}
    },
    ipdRegister: {
        type: Object || null,
        default: () => {}
    },
    bill: {
      type: Object || null,
      default: () => {}
    },
    generalId: {
        type: Number || null,
        default: null
    },
    defaultProductId: {
        type: Number || null,
        default: null
    },
    dentition: {
        type: Object || null,
        default: () => {}
    },
    dentitionType: {
        type: String || null,
        default: null,
    },
});

const emit = defineEmits(['update:dentitionType']);

const salesOrder = ref(null);
const companyId = computed(() => route.params.companyId);
const salesOrderId = computed(() => props.ipdRegister ? props.ipdRegister.sales_order_id : null);
const dentitionTypes = computed(() => props.dentition ? props.dentition.types : []);
const dentitionType = ref(props.dentitionType);
const billedDentition = ref({});

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    return query;
}

const setInitialData = () => {
    getSalesOrder();
}

const onChangeDentitionType = (value) => {
    emit('update:dentitionType', value);
}

const getSalesOrder = async () => {
    
    if(!salesOrderId.value ) return;

    await fetchSalesOrderById(salesOrderId.value, getQuery()).then(res => {
        if(res.data) {
            salesOrder.value = res.data;
            setSalesOrderProducts();
        }
    })
}

const setSalesOrderProducts = () => {
    
    for(const item of salesOrder.value.general) {
        
        const product = cloneDeep(item.product);
        product.quantity = item.quantity;
        product.rate = item.rate;
        product.vat = item.vat;
        product.discount = item.discount;
        product.discount_amount = item.discount_amount;
        product.discount_percent = item.discount_percent;
        product.vat_amount = item.vat_amount;
        product.has_prescription = item.prescription_count > 0 ? true : false;
        product.dentition = item.dentition_pertains ? pluck(item.dentition_pertains, 'tooth_number') : [];
        product.dentition_pertains = item.dentition_pertains || [];
        product.amount = item.amount;
        product.total_amount = item.total_amount;
        product.status = item.status;
        product.schedule_date = item.schedule_date;

        if(item.contact_profile_id) {
            product.service_resource = {
                id: item.contact_profile_id,
                name: item.contact_profile_name
            }
        }

        product.is_default_disabled = route.query.general_id ? true : false;

        props.treatmentPlan.selectedNotes.push(setProductData(product, item.id));

        item.dentition_pertains.filter(dentition => dentition.is_billed)
            .forEach(dentition => {
                if( ! billedDentition.value.hasOwnProperty(dentition.tooth_number) ) {
                    billedDentition.value[dentition.tooth_number] = [];
                }

                billedDentition.value[dentition.tooth_number].push(product);
            });
    }
}

const setDefaultProduct = async() => {
    
    if(!props.defaultProductId) {
        return;
    }

    const productResp = await getProduct({
        id: props.defaultProductId,
        company_id: companyId.value
    })

    if (!productResp.data) {
        showError("Something went wrong please try again");
        return;
    }

    let selected = productResp.data;
    selected.rate = selected.description.sales_price;
    selected.vat = selected.description.vat_rate;
    selected.quantity = 1;
    selected.is_default_disabled = true;
    selected.service_resource = props.bill.service_resource || null
    selected.service_resource.id = props.bill.service_resource.contact_profile_id || null
    selected.schedule_date = props.bill.start_date_time || null

    props.treatmentPlan.selectedNotes.unshift(setProductData(selected));
}

const setProductData = (product, id = null) => {
    const formattedProduct = {
        id: id,
        product_id: product.id,
        name: product.name,
        description: product.description && product.description.unit ? product.description.unit.description : '',
        service_resource: product.service_resource || null,
        quantity: product.quantity,
        rate: product.rate,
        discount: product.discount || 0,
        vat: product.vat,
        sub_total: product.amount || 0,
        discount_amount: product.discount_amount || 0,
        discount_percent: product.discount_percent || 0,
        vat_amount: product.vat_amount || 0,
        total: product.total_amount || 0,
        isEdit: false,
        product: product,
        has_prescription: product.has_prescription || false,
        is_default_disabled: product.is_default_disabled || false,
        dentition: product.dentition || [],
        status: product.status,
        schedule_date: product.schedule_date,
        dentition_pertains: product.dentition_pertains || []
    }
    return formattedProduct;
}

onMounted(async () => {
    if(! salesOrderId.value) {
        await setDefaultProduct();
    }

    setInitialData();
});

</script>
